import posthog from 'posthog-js';
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { EventCard as EventCardType, SportsEvent } from '../types';
import { getLeagueCount } from '../utils/leagueCountUtils';
import DepositModal from './DepositModal';
import Get15TPCModal from './Get15TPCModal';
import Get5TPCModal from './Get5TPCModal';
import './Header.css';
import HowItWorksModal from './HowItWorksModal';
import LoginModal from './LoginModal';
import { SPORTS_CATEGORIES } from './SportsNavSidebar';

interface HeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCategory?: string;
  selectedSubCategory?: string;
  onCategoryChange: (category: string) => void;
  onSubCategoryChange: (subCategory: string) => void;
  onShowActiveParlays: () => void;
  onShowProfile: () => void;
  selectedFilter?: string;
  onFilterChange?: (filter: string) => void;
  referralCode?: string;
  hasLiveGames?: boolean;
  sportsEvents?: SportsEvent[];
  sportsProps?: EventCardType[];
}

const Header: React.FC<HeaderProps> = ({ searchQuery, onSearchChange, selectedCategory, selectedSubCategory, onCategoryChange, onSubCategoryChange, onShowActiveParlays, onShowProfile, selectedFilter = '', onFilterChange, referralCode, hasLiveGames = false, sportsEvents = [], sportsProps = [] }) => {
  const { isAuthenticated, user, logout, refreshBalances } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isGet5TPCModalOpen, setIsGet5TPCModalOpen] = useState(false);
  const [isGet15TPCModalOpen, setIsGet15TPCModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTrendingDropdownOpen, setIsTrendingDropdownOpen] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);

  // Log initial state
  console.log('🏗️ Header component initialized:', {
    isTrendingDropdownOpen,
    selectedCategory,
    selectedSubCategory
  });
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const trendingDropdownRef = useRef<HTMLDivElement>(null);
  const moreDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const prevCategoryRef = useRef<string>(selectedCategory);
  const prevSubCategoryRef = useRef<string>(selectedSubCategory);

  // Detect mobile viewport (width < 600px for mobile devices)
  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      const isMobileDevice = width < 600;
      console.log('📱 Mobile detection:', { width, isMobileDevice, wasMobile: isMobile });
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);


  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (trendingDropdownRef.current && !trendingDropdownRef.current.contains(event.target as Node)) {
        setIsTrendingDropdownOpen(false);
      }
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(event.target as Node)) {
        setIsMoreDropdownOpen(false);
      }
    };

    if (!isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile]);

  // Handle '/' key to focus search
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/' && event.target !== searchInputRef.current) {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Auto-open signup modal when referral code is present and user is not authenticated
  useEffect(() => {
    if (referralCode && !isAuthenticated && !isLoginModalOpen) {
      // Set context for referral signup
      sessionStorage.setItem('authContext', 'referral-signup');
      sessionStorage.setItem('postAuthRedirectUrl', '/');
      sessionStorage.setItem('isSignup', 'true');
      setIsLoginModalOpen(true);
    }
  }, [referralCode, isAuthenticated, isLoginModalOpen]);

  // Restore dropdown open state from sessionStorage on mobile
  useEffect(() => {
    console.log('🔄 Restore effect triggered:', { isMobile, selectedCategory, selectedSubCategory });
    
    if (!isMobile) {
      console.log('📱 Not mobile, skipping restore');
      return;
    }

    try {
      const storedState = sessionStorage.getItem('filterDropdownOpen:mobile');
      console.log('💾 Stored state from sessionStorage:', storedState);
      
      if (storedState) {
        const { open, category, subCategory } = JSON.parse(storedState);
        console.log('📊 Parsed stored state:', { open, category, subCategory });
        console.log('🔍 Current props:', { selectedCategory, selectedSubCategory });
        
        // Only restore if we're on the same category/subcategory
        if (open && category === selectedCategory && subCategory === selectedSubCategory) {
          console.log('✅ Restoring dropdown state - setting to open');
          setIsTrendingDropdownOpen(true);
        } else {
          console.log('❌ Not restoring - conditions not met:', {
            open,
            categoryMatch: category === selectedCategory,
            subCategoryMatch: subCategory === selectedSubCategory
          });
        }
      } else {
        console.log('📭 No stored state found');
      }
    } catch (error) {
      console.error('❌ Error restoring filter dropdown state:', error);
    }
  }, [isMobile, selectedCategory, selectedSubCategory]);

  // Persist dropdown state to sessionStorage on mobile
  useEffect(() => {
    console.log('💾 Persist effect triggered:', { 
      isMobile, 
      isTrendingDropdownOpen, 
      selectedCategory, 
      selectedSubCategory 
    });
    
    if (!isMobile) {
      console.log('📱 Not mobile, skipping persist');
      return;
    }

    try {
      const stateData = {
        open: isTrendingDropdownOpen,
        category: selectedCategory,
        subCategory: selectedSubCategory
      };
      console.log('💾 Saving to sessionStorage:', stateData);
      sessionStorage.setItem('filterDropdownOpen:mobile', JSON.stringify(stateData));
      console.log('✅ Successfully saved to sessionStorage');
    } catch (error) {
      console.error('❌ Error persisting filter dropdown state:', error);
    }
  }, [isMobile, isTrendingDropdownOpen, selectedCategory, selectedSubCategory]);

  // Clear dropdown state when category/subcategory changes
  useEffect(() => {
    console.log('🧹 Clear effect triggered:', { 
      isMobile, 
      selectedCategory, 
      selectedSubCategory,
      prevCategory: prevCategoryRef.current,
      prevSubCategory: prevSubCategoryRef.current
    });
    
    if (!isMobile) {
      console.log('📱 Not mobile, skipping clear');
      return;
    }

    // Only clear if this is a category/subcategory change, not initial load
    const categoryChanged = prevCategoryRef.current !== selectedCategory;
    const subCategoryChanged = prevSubCategoryRef.current !== selectedSubCategory;
    
    if (categoryChanged || subCategoryChanged) {
      console.log('🧹 Category/subcategory changed, clearing sessionStorage and closing dropdown');
      try {
        sessionStorage.removeItem('filterDropdownOpen:mobile');
        setIsTrendingDropdownOpen(false);
        console.log('✅ Successfully cleared sessionStorage and closed dropdown');
      } catch (error) {
        console.error('❌ Error clearing filter dropdown state:', error);
      }
    } else {
      console.log('🧹 No category/subcategory change detected, skipping clear');
    }
    
    // Update refs for next comparison
    prevCategoryRef.current = selectedCategory;
    prevSubCategoryRef.current = selectedSubCategory;
  }, [isMobile, selectedCategory, selectedSubCategory]);

  const handleLoginClick = () => {
    // Set context for home page login
    sessionStorage.setItem('authContext', 'home-page');
    sessionStorage.setItem('postAuthRedirectUrl', '/');
    sessionStorage.setItem('isSignup', 'false');
    setIsLoginModalOpen(true);
  };

  const handleSignupClick = () => {
    // Set context for home page signup
    sessionStorage.setItem('authContext', 'home-page');
    sessionStorage.setItem('postAuthRedirectUrl', '/');
    sessionStorage.setItem('isSignup', 'true');
    setIsLoginModalOpen(true);
  };

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = async () => {
    try { posthog.capture('engagement:dropdown_logout'); } catch {}
    await logout();
    setIsUserMenuOpen(false);
  };

  const handleDepositClick = () => {
    setIsDepositModalOpen(true);
  };

  const handleRefreshBalances = async () => {
    if (refreshBalances) {
      try {
        await refreshBalances();
        console.log('Balances refreshed successfully');
      } catch (error) {
        console.error('Failed to refresh balances:', error);
      }
    }
  };

  const formatCurrency = (value: string | undefined) => {
    if (!value) return '$0.00';
    // Remove any existing dollar sign and parse the number
    const numericValue = parseFloat(value.replace('$', ''));
    if (isNaN(numericValue)) return value; // Return original if not a valid number
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numericValue);
  };

  return (
    <div className={`header-container ${isMobile && selectedCategory === 'Earnings' ? 'earnings-mobile' : ''}`}>
      <div className="header-top">
        <button 
          className="logo"
          onClick={() => window.location.href = '/'}
        >
          <img 
            src="/TPC_straight.png" 
            alt="The Parlay Company" 
            className="logo-image"
          />
        </button>
        
        <div className="header-right">
          {/* Hide search on mobile when Sports or Earnings is selected */}
          <div className={`search-container ${(selectedCategory === 'Sports' || selectedCategory === 'Earnings') ? 'mobile-hide-search' : ''}`}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="search-icon" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
            </svg>
            <input 
              type="text" 
              placeholder="Search markets or profiles" 
              className="search-input"
              value={searchQuery}
              onChange={(e) => { const q = e.target.value; onSearchChange(q); try { posthog.capture('engagement:search', { query: q }); } catch {} }}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              ref={searchInputRef}
            />
            {!isSearchFocused && (
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="slash-icon" viewBox="0 0 16 16">
                <path d="M9.854 2.146a.5.5 0 0 1 0 .708l-6 10a.5.5 0 0 1-.708-.708l6-10a.5.5 0 0 1 .708 0"/>
              </svg>
            )}
          </div>
          <button className="how-it-works-btn" onClick={() => { setIsHowItWorksModalOpen(true); try { posthog.capture('engagement:howtpcworks'); } catch {} }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12,17c0.6,0,1-0.4,1-1v-4c0-0.6-0.4-1-1-1s-1,0.4-1,1v4C11,16.6,11.4,17,12,17z" fill="white"/>
              <circle cx="12" cy="8" r="1" fill="white"/>
            </svg>
            How TPC works
          </button>
          {!isAuthenticated && (
            <button className="get-15tpc-btn" onClick={() => { setIsGet15TPCModalOpen(true); try { posthog.capture('engagement:15tpc'); } catch {} }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12,17c0.6,0,1-0.4,1-1v-4c0-0.6-0.4-1-1-1s-1,0.4-1,1v4C11,16.6,11.4,17,12,17z" fill="white"/>
                <circle cx="12" cy="8" r="1" fill="white"/>
              </svg>
              Get 15 $TPC
            </button>
          )}

          {isAuthenticated ? (
          <div className="authenticated-controls">
            <button className="get-5tpc-btn" onClick={() => { setIsGet5TPCModalOpen(true); try { posthog.capture('engagement:5tpc'); } catch {} }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12,17c0.6,0,1-0.4,1-1v-4c0-0.6-0.4-1-1-1s-1,0.4-1,1v4C11,16.6,11.4,17,12,17z" fill="white"/>
                <circle cx="12" cy="8" r="1" fill="white"/>
              </svg>
              Get 5 $TPC
            </button>
            <div className="portfolio-display" onClick={handleRefreshBalances} style={{cursor: 'pointer'}} title="Click to refresh balances">
              <div className="balance-item">
                <span className="balance-label">Portfolio</span>
                <span className="balance-value">{formatCurrency(user?.portfolioBalance)}</span>
              </div>
              <div className="balance-item">
                <span className="balance-label">Cash</span>
                <span className="balance-value">{formatCurrency(user?.cashBalance)}</span>
              </div>
            </div>
            <button className="deposit-button" onClick={handleDepositClick} style={{ display: 'none' }}>Deposit</button>
            <div className="user-menu">
              <button className="user-avatar" onClick={handleUserMenuToggle}>
                <div className="avatar-circle">
                  {user?.email?.charAt(0).toUpperCase()}
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="dropdown-arrow">
                  <polyline points="6,9 12,15 18,9"/>
                </svg>
              </button>
              
              {isUserMenuOpen && (
                <div className="user-dropdown">
                  <div className="user-info">
                    <p className="user-email">{user?.email}</p>
                    <div className="wallet-address-container">
                      <p className="wallet-address">
                        {user?.walletAddress ? 
                          `${user.walletAddress.slice(0, 6)}...${user.walletAddress.slice(-4)}` : 
                          '...'
                        }
                      </p>
                      <button className="copy-address-btn" onClick={() => navigator.clipboard.writeText(user?.walletAddress || '')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <hr className="dropdown-divider" />
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      try { posthog.capture('engagement:dropdown_profile'); } catch {}
                      onShowProfile();
                      setIsUserMenuOpen(false);
                    }}
                  >
                    Profile
                  </button>
                  {/* <button className="dropdown-item">Watchlist</button> */}
                  {/* <button className="dropdown-item">Rewards</button> */}
                  {/* <button className="dropdown-item">Documentation</button> */}
                  <button 
                    className="dropdown-item active-parlays-menu-item" 
                    onClick={() => {
                      try { posthog.capture('engagement:dropdown_parlays'); } catch {}
                      onShowActiveParlays();
                      setIsUserMenuOpen(false);
                    }}
                  >
                    Parlays
                  </button>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      window.open('https://tpccreators.com/', '_blank');
                      setIsUserMenuOpen(false);
                    }}
                  >
                    TPC Creators
                  </button>
                  <button 
                    className="dropdown-item"
                    onClick={() => {
                      window.open('https://docs.google.com/document/d/1ybQTWpo4lbXmDNc1tYCisBrtYXNWuiKoGW1EKIxXoo4/edit?usp=sharing', '_blank');
                      setIsUserMenuOpen(false);
                    }}
                  >
                    Terms of Use
                  </button>
                 
                  <hr className="dropdown-divider" />
                  <button className="dropdown-item" onClick={handleLogout}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
          ) : (
            <div className="auth-buttons">
              <button className="login-button" onClick={handleLoginClick}>Log In</button>
              <button className="signup-button" onClick={handleSignupClick}>Sign Up</button>
            </div>
          )}
        </div>
      </div>
      
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        referralCode={referralCode}
        isSignup={sessionStorage.getItem('isSignup') === 'true'}
        onRequireSignup={handleSignupClick}
      />
      
      <DepositModal 
        isOpen={isDepositModalOpen} 
        onClose={() => setIsDepositModalOpen(false)} 
      />

      <HowItWorksModal
        isOpen={isHowItWorksModalOpen}
        onClose={() => setIsHowItWorksModalOpen(false)}
        onOpenLogin={() => setIsLoginModalOpen(true)}
      />

      <Get5TPCModal
        isOpen={isGet5TPCModalOpen}
        onClose={() => setIsGet5TPCModalOpen(false)}
      />

      <Get15TPCModal
        isOpen={isGet15TPCModalOpen}
        onClose={() => setIsGet15TPCModalOpen(false)}
        onOpenLogin={() => setIsLoginModalOpen(true)}
      />
      <div className={`categories-nav ${selectedCategory === 'Sports' ? 'sports-primary' : ''}`}>
        <button 
          className={`category-button ${selectedFilter === 'Trending' && selectedCategory === '' ? 'active' : ''}`}
          onClick={() => {
            onCategoryChange(''); // Reset to "All" categories
            onFilterChange?.('Trending'); // Apply Trending filter
            try { posthog.capture('engagement:event_filter_selected', { filter: 'Trending', category: '', sub_category: '' }); } catch {}
            try { posthog.capture('engagement:primary_category_selected', { category: 'Trending' }); } catch {}
          }}
        >
          Trending
        </button>
        <button 
          className={`category-button ${selectedFilter === 'Newest' && selectedCategory === '' ? 'active' : ''}`}
          onClick={() => {
            onCategoryChange(''); // Reset to "All" categories
            onFilterChange?.('Newest'); // Apply Newest filter
            try { posthog.capture('engagement:event_filter_selected', { filter: 'Newest', category: '', sub_category: '' }); } catch {}
          }}
        >
          New
        </button>
        <span className="category-divider" role="separator" aria-hidden="true"></span>
        <button 
          className={`category-button ${selectedCategory === '' && selectedFilter !== 'Newest' && selectedFilter !== 'Trending' ? 'active' : ''}`}
          onClick={() => { onCategoryChange(''); try { posthog.capture('engagement:primary_category_selected', { category: 'All' }); } catch {} }}
        >
          All
        </button>
        <button 
          className={`category-button ${selectedCategory === 'Politics' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Politics'); try { posthog.capture('engagement:primary_category_selected', { category: 'Politics' }); } catch {} }}
        >
          Politics
        </button>
        <button 
          className={`category-button ${selectedCategory === 'Finance' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Finance'); try { posthog.capture('engagement:primary_category_selected', { category: 'Finance' }); } catch {} }}
        >
          Finance
        </button>
        <button 
          className={`category-button ${selectedCategory === 'Sports' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Sports'); try { posthog.capture('engagement:primary_category_selected', { category: 'Sports' }); } catch {} }}
        >
          Sports
        </button>
        <button 
          className={`category-button ${selectedCategory === 'Culture' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Culture'); try { posthog.capture('engagement:primary_category_selected', { category: 'Culture' }); } catch {} }}
        >
          Culture
        </button>
        <button 
          className={`category-button ${selectedCategory === 'Crypto' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Crypto'); try { posthog.capture('engagement:primary_category_selected', { category: 'Crypto' }); } catch {} }}
        >
          Crypto
        </button>
        <button
          className={`category-button ${selectedCategory === 'Mentions' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Mentions'); try { posthog.capture('engagement:primary_category_selected', { category: 'Mentions' }); } catch {} }}
        >
          Mentions
        </button>
        <button
          className={`category-button ${selectedCategory === 'Economics' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Economics'); try { posthog.capture('engagement:primary_category_selected', { category: 'Economics' }); } catch {} }}
        >
          Economics
        </button>
        <button 
          className={`category-button ${selectedCategory === 'Geopolitics' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Geopolitics'); try { posthog.capture('engagement:primary_category_selected', { category: 'Geopolitics' }); } catch {} }}
        >
          Geopolitics
        </button>
        <button 
          className={`category-button ${selectedCategory === 'Tech' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Tech'); try { posthog.capture('engagement:primary_category_selected', { category: 'Tech' }); } catch {} }}
        >
          Tech
        </button>
        <button
          className={`category-button ${selectedCategory === 'Earnings' ? 'active' : ''}`}
          onClick={() => { onCategoryChange('Earnings'); try { posthog.capture('engagement:primary_category_selected', { category: 'Earnings' }); } catch {} }}
        >
          Earnings
        </button>
        <div className="more-dropdown-container" ref={moreDropdownRef}>
          <button
            className={`category-button ${selectedCategory === 'Elections' || selectedCategory === 'World' || selectedCategory === 'Leaderboard' ? 'active' : ''}`}
            onClick={() => setIsMoreDropdownOpen(!isMoreDropdownOpen)}
          >
            More
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '4px' }}>
              <polyline points="6,9 12,15 18,9"/>
            </svg>
          </button>
          {isMoreDropdownOpen && (
            <div className="more-dropdown-menu">
              <button
                className="more-dropdown-item"
                onClick={() => {
                  onCategoryChange('Elections');
                  setIsMoreDropdownOpen(false);
                  try { posthog.capture('engagement:primary_category_selected', { category: 'Elections' }); } catch {}
                }}
              >
                Elections
              </button>
              <button
                className="more-dropdown-item"
                onClick={() => {
                  onCategoryChange('World');
                  setIsMoreDropdownOpen(false);
                  try { posthog.capture('engagement:primary_category_selected', { category: 'World' }); } catch {}
                }}
              >
                World
              </button>
              <button
                className="more-dropdown-item"
                onClick={() => {
                  onCategoryChange('Leaderboard');
                  setIsMoreDropdownOpen(false);
                  try { posthog.capture('engagement:primary_category_selected', { category: 'Leaderboard' }); } catch {}
                }}
              >
                Leaderboard
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="categories-divider"></div>
      {/* Mobile-only top sports leagues nav (uses existing sidebar data) */}
      {selectedCategory === 'Sports' && (
        <div className="sports-top-leagues-nav">
          {/* Live and All first, then flat list of leagues (exclude Football/Soccer parents) */}
          {/* Live */}
          {hasLiveGames && (
            <div className="league-with-icon">
              <span className="league-icon live-dot" aria-hidden="true"></span>
              <button 
                className={`sports-league-button ${selectedSubCategory === 'Live' ? 'active' : ''}`}
                onClick={() => { onSubCategoryChange('Live'); try { posthog.capture('engagement:secondary_category_selected', { category: 'Sports', sub_category: 'Live' }); } catch {} }}
              >
                Live
              </button>
            </div>
          )}
          {/* All (hidden on mobile, default still set in logic) */}
          {!isMobile && (
            <button 
              className={`sports-league-button ${selectedSubCategory === '' ? 'active' : ''}`}
              onClick={() => { onSubCategoryChange(''); try { posthog.capture('engagement:secondary_category_selected', { category: 'Sports', sub_category: 'All' }); } catch {} }}
            >
              All
            </button>
          )}
          {/* Flat leagues */}
          {SPORTS_CATEGORIES.filter(c => !c.subcategories && c.displayName !== 'Rugby').map((league) => (
            league.displayName === 'NFL' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="matrix(-1, 0, 0, 1, 0, 0)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M5.97014 8.17779C7.26162 8.65722 8.42967 9.62878 8.97582 10.8339C9.12507 11.1632 9.08493 11.5474 8.87084 11.8388C8.83199 11.8917 8.79426 11.9454 8.7577 12H8.76898H8.79479H8.82056H8.84628H8.87195H8.89758H8.92316H8.94868H8.97416H8.99959H9.02496H9.05028H9.07555H9.10077H9.12593H9.15103H9.17607H9.20106H9.226H9.25087H9.27568H9.30043H9.32512H9.34975H9.37432H9.39882H9.42326H9.44763H9.47194H9.49618H9.52035H9.54445H9.56849H9.59245H9.61635H9.64017H9.66392H9.6876H9.7112H9.73473H9.75819H9.78157H9.80487H9.82809H9.85124H9.8743H9.89729H9.92019H9.94302H9.96576H9.98841H10.011H10.0335H10.0559H10.0782H10.1004H10.1226H10.1446H10.1666H10.1885H10.2102H10.2319H10.2535H10.275H10.2965H10.3178H10.339H10.3601H10.3811H10.4021H10.4229H10.4436H10.4643H10.4848H10.5052H10.5255H10.5458H10.5659H10.5859H10.6058H10.6256H10.6453H10.6649H10.6843H10.7037H10.723H10.7421H10.7611H10.78H10.7989H10.8175H10.8361H10.8546H10.8729H10.8911H10.9092H10.9272H10.9451H10.9628H10.9805H10.998H11.0153H11.0326H11.0497H11.0667H11.0836H11.1004H11.117H11.1335H11.1498H11.1661H11.1822H11.1982H11.214H11.2297H11.2453H11.2607H11.2675C11.6133 11.4022 12.2597 11 13 11C14.1045 11 15 11.8954 15 13C15 14.1046 14.1045 15 13 15C12.8335 15 12.6718 14.9797 12.5172 14.9413L10.4922 17.3713C10.4755 17.7616 10.4313 18.1234 10.3571 18.458C10.9943 18.8037 11.7242 19 12.5001 19C13.8971 19 15.1449 18.3646 15.9718 17.3632C16.1618 17.1332 16.4446 17 16.7429 17H19.2454C20.3439 15.63 21 13.8927 21 12C21 7.58172 17.4183 4 13 4C9.9673 4 7.32685 5.68789 5.97014 8.17779ZM9.44862 20.2407C10.3585 20.7253 11.3972 21 12.5001 21C14.3437 21 16.0086 20.2314 17.1905 19H19.7083C19.9928 19 20.2638 18.8789 20.4535 18.6669C22.0363 16.8984 23 14.5605 23 12C23 6.47715 18.5229 2 13 2C8.69964 2 5.03568 4.71401 3.62272 8.51929C3.51439 8.81104 3.54807 9.13661 3.71383 9.40001C3.8796 9.66341 4.15855 9.83462 4.46846 9.86316C5.39892 9.94887 6.35931 10.5204 6.88808 11.2187C6.74151 11.4689 6.61117 11.7298 6.49852 12H6.49105H6.46424H6.43745H6.41069H6.38395H6.35723H6.33054H6.30388H6.27724H6.25063H6.22405H6.1975H6.17098H6.1445H6.11804H6.09161H6.06522H6.03887H6.01254H5.98626H5.96001H5.9338H5.90762H5.88149H5.85539H5.82934H5.80332H5.77735H5.75142H5.72553H5.69969H5.6739H5.64815H5.62244H5.59679H5.57118H5.54562H5.52011H5.49465H5.46925H5.44389H5.41859H5.39334H5.36815H5.34301H5.31793H5.2929H5.26793H5.24302H5.21817H5.19338H5.16865H5.14399H5.11938H5.09484H5.07036H5.04595H5.0216H4.99732H4.9731H4.94895H4.92487H4.90087H4.87693H4.85306H4.82926H4.80554H4.78189H4.75831H4.73481H4.71138H4.68803H4.66476H4.64157H4.61845H4.59542H4.57246H4.54958H4.52679H4.50408H4.48146H4.45891H4.43646H4.41408H4.3918H4.3696H4.34749H4.32547H4.30354H4.2817H4.25995H4.2383H4.21673H4.19526H4.17388H4.1526H4.13142H4.11033H4.08934H4.06845H4.04766H4.02696H4.00637H3.98588H3.96549H3.9452H3.92502H3.90494H3.88497H3.86511H3.84535H3.8257H3.80615H3.78672H3.7674H3.74818H3.72908H3.71009H3.69122H3.67246H3.65381H3.63528H3.61686H3.59857H3.58039H3.56233H3.54438H3.52656H3.50886H3.49128H3.47383H3.45649H3.43929H3.4222H3.40524H3.38841H3.37171H3.35513H3.33869H3.32237H3.30618H3.29012H3.2742H3.25841H3.24275H3.22723H3.21184H3.19659H3.18147H3.16649H3.15165H3.13695H3.12239H3.10797H3.09369H3.07955H3.06556H3.05171H3.038H3.02444H3.01102H2.99776H2.98463H2.97166H2.95884H2.94617H2.93364H2.92127H2.90905H2.89699H2.88508H2.87332H2.86172H2.85027H2.83899H2.82786H2.81688H2.80607H2.79542H2.78493H2.7746H2.76443H2.75443H2.74459H2.73491H2.7254H2.71606H2.70688H2.69787H2.68903H2.68037H2.67187H2.66354H2.65538H2.6474H2.63959H2.63195H2.62449H2.6172H2.61009H2.60316H2.59641H2.58983H2.58344H2.57722H2.57119H2.56534H2.55967H2.55419H2.54889H2.54377H2.53884H2.5341H2.52954H2.52518H2.521H2.51701H2.51322H2.50961H2.5062H2.50297H2.49995C2.10492 12 1.74692 12.2325 1.58632 12.5934C1.27342 13.2966 1.0824 14.2702 0.989671 15.1582C0.896829 16.0473 0.886885 17.0028 1.01771 17.6876C1.22951 18.7964 1.64804 19.7661 2.00391 20.4489C2.18334 20.7931 2.35067 21.0717 2.47505 21.2668C2.53734 21.3646 2.58915 21.4419 2.6267 21.4965C2.64549 21.5238 2.66073 21.5456 2.67197 21.5614L2.68576 21.5807L2.6903 21.5869L2.69196 21.5892L2.69264 21.5901L2.69294 21.5905C2.69307 21.5907 2.69321 21.5909 3.49995 21L2.69321 21.5909C2.88156 21.8481 3.18121 22 3.49995 22L3.49995 21C3.49995 22 3.50008 22 3.50022 22H3.50053H3.5013L3.50338 22L3.50972 22L3.53091 21.9998C3.54874 21.9997 3.57389 21.9994 3.60576 21.9989C3.66947 21.9978 3.76017 21.9957 3.8729 21.9915C4.09794 21.9832 4.41303 21.9666 4.77798 21.9334C5.49441 21.8683 6.45832 21.7346 7.31618 21.4487C8.04196 21.2068 8.83624 20.8844 9.44862 20.2407ZM7.12791 18.1604C7.33641 18.4658 7.57028 18.7525 7.82648 19.0175C7.57714 19.2094 7.22112 19.3722 6.68372 19.5513C6.04157 19.7654 5.25548 19.8817 4.59691 19.9416C4.38665 19.9607 4.19503 19.9736 4.03258 19.9823C3.95614 19.8539 3.86895 19.7 3.77745 19.5245C3.6131 19.2092 3.43942 18.8328 3.28905 18.4163L7.12791 18.1604ZM8.2848 16.0788L9.00598 16.0307L10.6982 14H10.6843H10.6649H10.6453H10.6256H10.6058H10.5859H10.5659H10.5458H10.5255H10.5052H10.4848H10.4643H10.4436H10.4229H10.4021H10.3811H10.3601H10.339H10.3178H10.2965H10.275H10.2535H10.2319H10.2102H10.1885H10.1666H10.1446H10.1226H10.1004H10.0782H10.0559H10.0335H10.011H9.98841H9.96576H9.94302H9.92019H9.89729H9.8743H9.85124H9.82809H9.80487H9.78157H9.75819H9.73473H9.7112H9.6876H9.66392H9.64017H9.61635H9.59245H9.56849H9.54445H9.52035H9.49618H9.47194H9.44763H9.42326H9.39882H9.37432H9.34975H9.32512H9.30043H9.27568H9.25087H9.226H9.20106H9.17607H9.15103H9.12593H9.10077H9.07555H9.05028H9.02496H8.99959H8.97416H8.94868H8.92316H8.89758H8.87195H8.84628H8.82056H8.79479H8.76898H8.74312H8.71722H8.69127H8.66529H8.63926H8.61319H8.58708H8.56093H8.53474H8.50851H8.48225H8.45595H8.42961H8.40324H8.37684H8.3504H8.32393H8.29743H8.2709H8.24434H8.21775H8.19113H8.16449H8.13781H8.11111H8.08439H8.05764H8.03087H8.02745C8.00935 14.1641 8.00006 14.3309 8.00006 14.5C8.00006 15.0555 8.10072 15.5875 8.2848 16.0788ZM6.01904 14C6.00646 14.1651 6.00006 14.3318 6.00006 14.5C6.00006 15.0939 6.07971 15.6692 6.22892 16.2158L2.9197 16.4365C2.92014 16.1142 2.93892 15.7484 2.97886 15.3659C3.03152 14.8616 3.11488 14.3874 3.21559 14H3.22723H3.24275H3.25841H3.2742H3.29012H3.30618H3.32237H3.33869H3.35513H3.37171H3.38841H3.40524H3.4222H3.43929H3.45649H3.47383H3.49128H3.50886H3.52656H3.54438H3.56233H3.58039H3.59857H3.61686H3.63528H3.65381H3.67246H3.69122H3.71009H3.72908H3.74818H3.7674H3.78672H3.80615H3.8257H3.84535H3.86511H3.88497H3.90494H3.92502H3.9452H3.96549H3.98588H4.00637H4.02696H4.04766H4.06845H4.08934H4.11033H4.13142H4.1526H4.17388H4.19526H4.21673H4.2383H4.25995H4.2817H4.30354H4.32547H4.34749H4.3696H4.3918H4.41408H4.43646H4.45891H4.48146H4.50408H4.52679H4.54958H4.57246H4.59542H4.61845H4.64157H4.66476H4.68803H4.71138H4.73481H4.75831H4.78189H4.80554H4.82926H4.85306H4.87693H4.90087H4.92487H4.94895H4.9731H4.99732H5.0216H5.04595H5.07036H5.09484H5.11938H5.14399H5.16865H5.19338H5.21817H5.24302H5.26793H5.2929H5.31793H5.34301H5.36815H5.39334H5.41859H5.44389H5.46925H5.49465H5.52011H5.54562H5.57118H5.59679H5.62244H5.64815H5.6739H5.69969H5.72553H5.75142H5.77735H5.80332H5.82934H5.85539H5.88149H5.90762H5.9338H5.96001H5.98626H6.01254H6.01904Z" fill="#0008ff"></path> </g></svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => { onSubCategoryChange(league.apiKey || league.displayName); try { posthog.capture('engagement:secondary_category_selected', { category: 'Sports', sub_category: (league.apiKey || league.displayName) }); } catch {} }}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'College Football' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="matrix(-1, 0, 0, 1, 0, 0)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M5.97014 8.17779C7.26162 8.65722 8.42967 9.62878 8.97582 10.8339C9.12507 11.1632 9.08493 11.5474 8.87084 11.8388C8.83199 11.8917 8.79426 11.9454 8.7577 12H8.76898H8.79479H8.82056H8.84628H8.87195H8.89758H8.92316H8.94868H8.97416H8.99959H9.02496H9.05028H9.07555H9.10077H9.12593H9.15103H9.17607H9.20106H9.226H9.25087H9.27568H9.30043H9.32512H9.34975H9.37432H9.39882H9.42326H9.44763H9.47194H9.49618H9.52035H9.54445H9.56849H9.59245H9.61635H9.64017H9.66392H9.6876H9.7112H9.73473H9.75819H9.78157H9.80487H9.82809H9.85124H9.8743H9.89729H9.92019H9.94302H9.96576H9.98841H10.011H10.0335H10.0559H10.0782H10.1004H10.1226H10.1446H10.1666H10.1885H10.2102H10.2319H10.2535H10.275H10.2965H10.3178H10.339H10.3601H10.3811H10.4021H10.4229H10.4436H10.4643H10.4848H10.5052H10.5255H10.5458H10.5659H10.5859H10.6058H10.6256H10.6453H10.6649H10.6843H10.7037H10.723H10.7421H10.7611H10.78H10.7989H10.8175H10.8361H10.8546H10.8729H10.8911H10.9092H10.9272H10.9451H10.9628H10.9805H10.998H11.0153H11.0326H11.0497H11.0667H11.0836H11.1004H11.117H11.1335H11.1498H11.1661H11.1822H11.1982H11.214H11.2297H11.2453H11.2607H11.2675C11.6133 11.4022 12.2597 11 13 11C14.1045 11 15 11.8954 15 13C15 14.1046 14.1045 15 13 15C12.8335 15 12.6718 14.9797 12.5172 14.9413L10.4922 17.3713C10.4755 17.7616 10.4313 18.1234 10.3571 18.458C10.9943 18.8037 11.7242 19 12.5001 19C13.8971 19 15.1449 18.3646 15.9718 17.3632C16.1618 17.1332 16.4446 17 16.7429 17H19.2454C20.3439 15.63 21 13.8927 21 12C21 7.58172 17.4183 4 13 4C9.9673 4 7.32685 5.68789 5.97014 8.17779ZM9.44862 20.2407C10.3585 20.7253 11.3972 21 12.5001 21C14.3437 21 16.0086 20.2314 17.1905 19H19.7083C19.9928 19 20.2638 18.8789 20.4535 18.6669C22.0363 16.8984 23 14.5605 23 12C23 6.47715 18.5229 2 13 2C8.69964 2 5.03568 4.71401 3.62272 8.51929C3.51439 8.81104 3.54807 9.13661 3.71383 9.40001C3.8796 9.66341 4.15855 9.83462 4.46846 9.86316C5.39892 9.94887 6.35931 10.5204 6.88808 11.2187C6.74151 11.4689 6.61117 11.7298 6.49852 12H6.49105H6.46424H6.43745H6.41069H6.38395H6.35723H6.33054H6.30388H6.27724H6.25063H6.22405H6.1975H6.17098H6.1445H6.11804H6.09161H6.06522H6.03887H6.01254H5.98626H5.96001H5.9338H5.90762H5.88149H5.85539H5.82934H5.80332H5.77735H5.75142H5.72553H5.69969H5.6739H5.64815H5.62244H5.59679H5.57118H5.54562H5.52011H5.49465H5.46925H5.44389H5.41859H5.39334H5.36815H5.34301H5.31793H5.2929H5.26793H5.24302H5.21817H5.19338H5.16865H5.14399H5.11938H5.09484H5.07036H5.04595H5.0216H4.99732H4.9731H4.94895H4.92487H4.90087H4.87693H4.85306H4.82926H4.80554H4.78189H4.75831H4.73481H4.71138H4.68803H4.66476H4.64157H4.61845H4.59542H4.57246H4.54958H4.52679H4.50408H4.48146H4.45891H4.43646H4.41408H4.3918H4.3696H4.34749H4.32547H4.30354H4.2817H4.25995H4.2383H4.21673H4.19526H4.17388H4.1526H4.13142H4.11033H4.08934H4.06845H4.04766H4.02696H4.00637H3.98588H3.96549H3.9452H3.92502H3.90494H3.88497H3.86511H3.84535H3.8257H3.80615H3.78672H3.7674H3.74818H3.72908H3.71009H3.69122H3.67246H3.65381H3.63528H3.61686H3.59857H3.58039H3.56233H3.54438H3.52656H3.50886H3.49128H3.47383H3.45649H3.43929H3.4222H3.40524H3.38841H3.37171H3.35513H3.33869H3.32237H3.30618H3.29012H3.2742H3.25841H3.24275H3.22723H3.21184H3.19659H3.18147H3.16649H3.15165H3.13695H3.12239H3.10797H3.09369H3.07955H3.06556H3.05171H3.038H3.02444H3.01102H2.99776H2.98463H2.97166H2.95884H2.94617H2.93364H2.92127H2.90905H2.89699H2.88508H2.87332H2.86172H2.85027H2.83899H2.82786H2.81688H2.80607H2.79542H2.78493H2.7746H2.76443H2.75443H2.74459H2.73491H2.7254H2.71606H2.70688H2.69787H2.68903H2.68037H2.67187H2.66354H2.65538H2.6474H2.63959H2.63195H2.62449H2.6172H2.61009H2.60316H2.59641H2.58983H2.58344H2.57722H2.57119H2.56534H2.55967H2.55419H2.54889H2.54377H2.53884H2.5341H2.52954H2.52518H2.521H2.51701H2.51322H2.50961H2.5062H2.50297H2.49995C2.10492 12 1.74692 12.2325 1.58632 12.5934C1.27342 13.2966 1.0824 14.2702 0.989671 15.1582C0.896829 16.0473 0.886885 17.0028 1.01771 17.6876C1.22951 18.7964 1.64804 19.7661 2.00391 20.4489C2.18334 20.7931 2.35067 21.0717 2.47505 21.2668C2.53734 21.3646 2.58915 21.4419 2.6267 21.4965C2.64549 21.5238 2.66073 21.5456 2.67197 21.5614L2.68576 21.5807L2.6903 21.5869L2.69196 21.5892L2.69264 21.5901L2.69294 21.5905C2.69307 21.5907 2.69321 21.5909 3.49995 21L2.69321 21.5909C2.88156 21.8481 3.18121 22 3.49995 22L3.49995 21C3.49995 22 3.50008 22 3.50022 22H3.50053H3.5013L3.50338 22L3.50972 22L3.53091 21.9998C3.54874 21.9997 3.57389 21.9994 3.60576 21.9989C3.66947 21.9978 3.76017 21.9957 3.8729 21.9915C4.09794 21.9832 4.41303 21.9666 4.77798 21.9334C5.49441 21.8683 6.45832 21.7346 7.31618 21.4487C8.04196 21.2068 8.83624 20.8844 9.44862 20.2407ZM7.12791 18.1604C7.33641 18.4658 7.57028 18.7525 7.82648 19.0175C7.57714 19.2094 7.22112 19.3722 6.68372 19.5513C6.04157 19.7654 5.25548 19.8817 4.59691 19.9416C4.38665 19.9607 4.19503 19.9736 4.03258 19.9823C3.95614 19.8539 3.86895 19.7 3.77745 19.5245C3.6131 19.2092 3.43942 18.8328 3.28905 18.4163L7.12791 18.1604ZM8.2848 16.0788L9.00598 16.0307L10.6982 14H10.6843H10.6649H10.6453H10.6256H10.6058H10.5859H10.5659H10.5458H10.5255H10.5052H10.4848H10.4643H10.4436H10.4229H10.4021H10.3811H10.3601H10.339H10.3178H10.2965H10.275H10.2535H10.2319H10.2102H10.1885H10.1666H10.1446H10.1226H10.1004H10.0782H10.0559H10.0335H10.011H9.98841H9.96576H9.94302H9.92019H9.89729H9.8743H9.85124H9.82809H9.80487H9.78157H9.75819H9.73473H9.7112H9.6876H9.66392H9.64017H9.61635H9.59245H9.56849H9.54445H9.52035H9.49618H9.47194H9.44763H9.42326H9.39882H9.37432H9.34975H9.32512H9.30043H9.27568H9.25087H9.226H9.20106H9.17607H9.15103H9.12593H9.10077H9.07555H9.05028H9.02496H8.99959H8.97416H8.94868H8.92316H8.89758H8.87195H8.84628H8.82056H8.79479H8.76898H8.74312H8.71722H8.69127H8.66529H8.63926H8.61319H8.58708H8.56093H8.53474H8.50851H8.48225H8.45595H8.42961H8.40324H8.37684H8.3504H8.32393H8.29743H8.2709H8.24434H8.21775H8.19113H8.16449H8.13781H8.11111H8.08439H8.05764H8.03087H8.02745C8.00935 14.1641 8.00006 14.3309 8.00006 14.5C8.00006 15.0555 8.10072 15.5875 8.2848 16.0788ZM6.01904 14C6.00646 14.1651 6.00006 14.3318 6.00006 14.5C6.00006 15.0939 6.07971 15.6692 6.22892 16.2158L2.9197 16.4365C2.92014 16.1142 2.93892 15.7484 2.97886 15.3659C3.03152 14.8616 3.11488 14.3874 3.21559 14H3.22723H3.24275H3.25841H3.2742H3.29012H3.30618H3.32237H3.33869H3.35513H3.37171H3.38841H3.40524H3.4222H3.43929H3.45649H3.47383H3.49128H3.50886H3.52656H3.54438H3.56233H3.58039H3.59857H3.61686H3.63528H3.65381H3.67246H3.69122H3.71009H3.72908H3.74818H3.7674H3.78672H3.80615H3.8257H3.84535H3.86511H3.88497H3.90494H3.92502H3.9452H3.96549H3.98588H4.00637H4.02696H4.04766H4.06845H4.08934H4.11033H4.13142H4.1526H4.17388H4.19526H4.21673H4.2383H4.25995H4.2817H4.30354H4.32547H4.34749H4.3696H4.3918H4.41408H4.43646H4.45891H4.48146H4.50408H4.52679H4.54958H4.57246H4.59542H4.61845H4.64157H4.66476H4.68803H4.71138H4.73481H4.75831H4.78189H4.80554H4.82926H4.85306H4.87693H4.90087H4.92487H4.94895H4.9731H4.99732H5.0216H5.04595H5.07036H5.09484H5.11938H5.14399H5.16865H5.19338H5.21817H5.24302H5.26793H5.2929H5.31793H5.34301H5.36815H5.39334H5.41859H5.44389H5.46925H5.49465H5.52011H5.54562H5.57118H5.59679H5.62244H5.64815H5.6739H5.69969H5.72553H5.75142H5.77735H5.80332H5.82934H5.85539H5.88149H5.90762H5.9338H5.96001H5.98626H6.01254H6.01904Z" fill="#0008ff"></path> </g></svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {isMobile ? 'CFB' : league.displayName}
                </button>
              </div>
            ) : league.displayName === 'NBA' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.33946 16.9997C6.10089 21.7826 12.2168 23.4214 16.9997 20.66C18.9493 19.5344 20.3765 17.8514 21.1962 15.9286C22.3875 13.1341 22.2958 9.83304 20.66 6.99972C19.0242 4.1664 16.2112 2.43642 13.1955 2.07088C11.1204 1.81935 8.94932 2.21386 6.99972 3.33946C2.21679 6.10089 0.578039 12.2168 3.33946 16.9997Z" stroke="#ff0000" strokeWidth="1.5"></path>
                    <path d="M16.9498 20.5732C16.9498 20.5732 16.0108 13.982 14.0005 10.5C11.9901 7.01798 7.05029 3.42676 7.05029 3.42676" stroke="#ff0000" strokeWidth="1.5" strokeLinecap="round"></path>
                    <path d="M21.8638 12.5803C16.4528 11.3933 9.05903 16.348 7.57739 20.8177" stroke="#ff0000" strokeWidth="1.5" strokeLinecap="round"></path>
                    <path d="M16.4141 3.20884C14.9262 7.6299 7.67443 12.5122 2.28877 11.4515" stroke="#ff0000" strokeWidth="1.5" strokeLinecap="round"></path>
                  </svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'Premier League' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#002aff" stroke="#002aff">
                    <g>
                      <path d="M464.16,107.059l0.067,0.084l-0.134-0.194c0.016,0.016,0.025,0.042,0.042,0.058 c-31.462-43.992-76.546-77.562-129.185-94.562l0.067,0.016C310.134,4.328,283.538,0,256,0c-27.53,0-54.135,4.328-79.009,12.47 l0.059-0.025c-52.631,17-97.714,50.571-129.177,94.571l0.033-0.042C17.824,148.756,0.092,200.168,0,255.621v0.034V256 c-0.008,57.134,18.748,110.016,50.495,152.537c30.984,41.748,74.387,73.74,124.882,90.446c-0.016,0-0.034,0-0.042-0.009 l0.354,0.118l-0.286-0.101C200.798,507.488,227.932,512,256,512c28.151,0,55.302-4.512,80.748-13.11 c50.403-16.638,93.899-48.63,124.882-90.394l-0.016,0.025C493.269,366,512.008,313.126,512,256v-0.345v-0.025 C511.916,200.235,494.302,148.84,464.16,107.059z M264.622,76.008l47.328-34.336c4.16,1.084,8.311,2.176,12.361,3.504l0.068,0.025 c45.462,14.681,84.529,43.748,111.756,81.832l0.068,0.092c2.504,3.479,4.823,7.101,7.126,10.723l-17.942,54.942l-101.512,33.184 l-59.252-43.21V76.008z M75.84,127.076l0.033-0.042c27.228-38.084,66.294-67.151,111.757-81.832l0.067-0.025 c4.05-1.328,8.202-2.42,12.361-3.504l47.352,34.361v106.731l-59.235,43.21L86.698,192.798l-18-54.95 C71,134.202,73.327,130.571,75.84,127.076z M86.336,398.244c-2.815-3.354-5.614-6.732-8.227-10.252l-0.034-0.042 C50.613,351.126,34.428,305.571,34.42,256v-0.311c0.008-4.344,0.277-8.614,0.529-12.891l46.428-33.639l101.538,33.202 l22.698,69.386l-62.765,86.496H86.336z M338.05,461.689c-4.008,1.588-8.025,3.185-12.142,4.538l-0.127,0.042 c-21.874,7.395-45.286,11.312-69.781,11.312c-24.395,0-47.832-3.916-69.74-11.244l-0.126-0.041 c-4.109-1.361-8.109-2.95-12.109-4.547L156.73,408.42l62.815-86.564h72.815l62.53,87.765L338.05,461.689z M477.58,256 c-0.009,49.58-16.194,95.142-43.58,131.966l-0.016,0.026c-2.614,3.52-5.412,6.89-8.227,10.252h-57.84l-61.521-86.345l22.74-69.547 l101.546-33.202l46.369,33.639c0.252,4.269,0.521,8.546,0.53,12.891V256z"/>
                    </g>
                  </svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'La Liga' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#002aff" stroke="#002aff">
                    <g>
                      <path d="M464.16,107.059l0.067,0.084l-0.134-0.194c0.016,0.016,0.025,0.042,0.042,0.058 c-31.462-43.992-76.546-77.562-129.185-94.562l0.067,0.016C310.134,4.328,283.538,0,256,0c-27.53,0-54.135,4.328-79.009,12.47 l0.059-0.025c-52.631,17-97.714,50.571-129.177,94.571l0.033-0.042C17.824,148.756,0.092,200.168,0,255.621v0.034V256 c-0.008,57.134,18.748,110.016,50.495,152.537c30.984,41.748,74.387,73.74,124.882,90.446c-0.016,0-0.034,0-0.042-0.009 l0.354,0.118l-0.286-0.101C200.798,507.488,227.932,512,256,512c28.151,0,55.302-4.512,80.748-13.11 c50.403-16.638,93.899-48.63,124.882-90.394l-0.016,0.025C493.269,366,512.008,313.126,512,256v-0.345v-0.025 C511.916,200.235,494.302,148.84,464.16,107.059z M264.622,76.008l47.328-34.336c4.16,1.084,8.311,2.176,12.361,3.504l0.068,0.025 c45.462,14.681,84.529,43.748,111.756,81.832l0.068,0.092c2.504,3.479,4.823,7.101,7.126,10.723l-17.942,54.942l-101.512,33.184 l-59.252-43.21V76.008z M75.84,127.076l0.033-0.042c27.228-38.084,66.294-67.151,111.757-81.832l0.067-0.025 c4.05-1.328,8.202-2.42,12.361-3.504l47.352,34.361v106.731l-59.235,43.21L86.698,192.798l-18-54.95 C71,134.202,73.327,130.571,75.84,127.076z M86.336,398.244c-2.815-3.354-5.614-6.732-8.227-10.252l-0.034-0.042 C50.613,351.126,34.428,305.571,34.42,256v-0.311c0.008-4.344,0.277-8.614,0.529-12.891l46.428-33.639l101.538,33.202 l22.698,69.386l-62.765,86.496H86.336z M338.05,461.689c-4.008,1.588-8.025,3.185-12.142,4.538l-0.127,0.042 c-21.874,7.395-45.286,11.312-69.781,11.312c-24.395,0-47.832-3.916-69.74-11.244l-0.126-0.041 c-4.109-1.361-8.109-2.95-12.109-4.547L156.73,408.42l62.815-86.564h72.815l62.53,87.765L338.05,461.689z M477.58,256 c-0.009,49.58-16.194,95.142-43.58,131.966l-0.016,0.026c-2.614,3.52-5.412,6.89-8.227,10.252h-57.84l-61.521-86.345l22.74-69.547 l101.546-33.202l46.369,33.639c0.252,4.269,0.521,8.546,0.53,12.891V256z"/>
                    </g>
                  </svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'Ligue 1' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#002aff" stroke="#002aff">
                    <g>
                      <path d="M464.16,107.059l0.067,0.084l-0.134-0.194c0.016,0.016,0.025,0.042,0.042,0.058 c-31.462-43.992-76.546-77.562-129.185-94.562l0.067,0.016C310.134,4.328,283.538,0,256,0c-27.53,0-54.135,4.328-79.009,12.47 l0.059-0.025c-52.631,17-97.714,50.571-129.177,94.571l0.033-0.042C17.824,148.756,0.092,200.168,0,255.621v0.034V256 c-0.008,57.134,18.748,110.016,50.495,152.537c30.984,41.748,74.387,73.74,124.882,90.446c-0.016,0-0.034,0-0.042-0.009 l0.354,0.118l-0.286-0.101C200.798,507.488,227.932,512,256,512c28.151,0,55.302-4.512,80.748-13.11 c50.403-16.638,93.899-48.63,124.882-90.394l-0.016,0.025C493.269,366,512.008,313.126,512,256v-0.345v-0.025 C511.916,200.235,494.302,148.84,464.16,107.059z M264.622,76.008l47.328-34.336c4.16,1.084,8.311,2.176,12.361,3.504l0.068,0.025 c45.462,14.681,84.529,43.748,111.756,81.832l0.068,0.092c2.504,3.479,4.823,7.101,7.126,10.723l-17.942,54.942l-101.512,33.184 l-59.252-43.21V76.008z M75.84,127.076l0.033-0.042c27.228-38.084,66.294-67.151,111.757-81.832l0.067-0.025 c4.05-1.328,8.202-2.42,12.361-3.504l47.352,34.361v106.731l-59.235,43.21L86.698,192.798l-18-54.95 C71,134.202,73.327,130.571,75.84,127.076z M86.336,398.244c-2.815-3.354-5.614-6.732-8.227-10.252l-0.034-0.042 C50.613,351.126,34.428,305.571,34.42,256v-0.311c0.008-4.344,0.277-8.614,0.529-12.891l46.428-33.639l101.538,33.202 l22.698,69.386l-62.765,86.496H86.336z M338.05,461.689c-4.008,1.588-8.025,3.185-12.142,4.538l-0.127,0.042 c-21.874,7.395-45.286,11.312-69.781,11.312c-24.395,0-47.832-3.916-69.74-11.244l-0.126-0.041 c-4.109-1.361-8.109-2.95-12.109-4.547L156.73,408.42l62.815-86.564h72.815l62.53,87.765L338.05,461.689z M477.58,256 c-0.009,49.58-16.194,95.142-43.58,131.966l-0.016,0.026c-2.614,3.52-5.412,6.89-8.227,10.252h-57.84l-61.521-86.345l22.74-69.547 l101.546-33.202l46.369,33.639c0.252,4.269,0.521,8.546,0.53,12.891V256z"/>
                    </g>
                  </svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'MLS' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#002aff" stroke="#002aff">
                    <g>
                      <path d="M464.16,107.059l0.067,0.084l-0.134-0.194c0.016,0.016,0.025,0.042,0.042,0.058 c-31.462-43.992-76.546-77.562-129.185-94.562l0.067,0.016C310.134,4.328,283.538,0,256,0c-27.53,0-54.135,4.328-79.009,12.47 l0.059-0.025c-52.631,17-97.714,50.571-129.177,94.571l0.033-0.042C17.824,148.756,0.092,200.168,0,255.621v0.034V256 c-0.008,57.134,18.748,110.016,50.495,152.537c30.984,41.748,74.387,73.74,124.882,90.446c-0.016,0-0.034,0-0.042-0.009 l0.354,0.118l-0.286-0.101C200.798,507.488,227.932,512,256,512c28.151,0,55.302-4.512,80.748-13.11 c50.403-16.638,93.899-48.63,124.882-90.394l-0.016,0.025C493.269,366,512.008,313.126,512,256v-0.345v-0.025 C511.916,200.235,494.302,148.84,464.16,107.059z M264.622,76.008l47.328-34.336c4.16,1.084,8.311,2.176,12.361,3.504l0.068,0.025 c45.462,14.681,84.529,43.748,111.756,81.832l0.068,0.092c2.504,3.479,4.823,7.101,7.126,10.723l-17.942,54.942l-101.512,33.184 l-59.252-43.21V76.008z M75.84,127.076l0.033-0.042c27.228-38.084,66.294-67.151,111.757-81.832l0.067-0.025 c4.05-1.328,8.202-2.42,12.361-3.504l47.352,34.361v106.731l-59.235,43.21L86.698,192.798l-18-54.95 C71,134.202,73.327,130.571,75.84,127.076z M86.336,398.244c-2.815-3.354-5.614-6.732-8.227-10.252l-0.034-0.042 C50.613,351.126,34.428,305.571,34.42,256v-0.311c0.008-4.344,0.277-8.614,0.529-12.891l46.428-33.639l101.538,33.202 l22.698,69.386l-62.765,86.496H86.336z M338.05,461.689c-4.008,1.588-8.025,3.185-12.142,4.538l-0.127,0.042 c-21.874,7.395-45.286,11.312-69.781,11.312c-24.395,0-47.832-3.916-69.74-11.244l-0.126-0.041 c-4.109-1.361-8.109-2.95-12.109-4.547L156.73,408.42l62.815-86.564h72.815l62.53,87.765L338.05,461.689z M477.58,256 c-0.009,49.58-16.194,95.142-43.58,131.966l-0.016,0.026c-2.614,3.52-5.412,6.89-8.227,10.252h-57.84l-61.521-86.345l22.74-69.547 l101.546-33.202l46.369,33.639c0.252,4.269,0.521,8.546,0.53,12.891V256z"/>
                    </g>
                  </svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'MLB' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(-45)">
                    <path d="M14.182 13.0781C15.318 11.6968 16 9.92803 16 8C16 6.07197 15.318 4.30324 14.182 2.92192C13.6429 3.43359 13.1852 4.0302 12.8303 4.69032L13.7037 5.00821L13.0197 6.8876L12.1462 6.5697C12.0504 7.03143 12 7.50983 12 8C12 8.49018 12.0504 8.9686 12.1463 9.43035L13.0197 9.11245L13.7037 10.9918L12.8303 11.3097C13.1852 11.9698 13.6429 12.5664 14.182 13.0781Z" fill="#ff0000"/>
                    <path d="M10.2511 10.1201L9.2609 10.4805L9.94494 12.3599L10.9353 11.9995C11.3915 12.9177 12 13.747 12.7277 14.4543C11.4032 15.4261 9.76863 16 8 16C6.23137 16 4.59679 15.4261 3.27234 14.4543C4.00001 13.747 4.60847 12.9177 5.06473 11.9995L6.05493 12.3599L6.73897 10.4805L5.74885 10.1201C5.91302 9.44027 6 8.73029 6 8C6 7.26971 5.91302 6.55974 5.74885 5.87988L6.73897 5.5195L6.05493 3.64012L5.06474 4.00052C4.60848 3.08227 4.00001 2.25299 3.27234 1.54571C4.59679 0.573931 6.23137 0 8 0C9.76863 0 11.4032 0.573931 12.7277 1.54571C12 2.25301 11.3915 3.0823 10.9352 4.00057L9.94494 3.64013L9.2609 5.51952L10.2511 5.87993C10.087 6.55978 10 7.26973 10 8C10 8.73028 10.087 9.44025 10.2511 10.1201Z" fill="#ff0000"/>
                    <path d="M1.81802 13.0781C2.35712 12.5664 2.81481 11.9698 3.16966 11.3097L2.29616 10.9918L2.9802 9.11243L3.85374 9.43037C3.94961 8.96861 4 8.49019 4 8C4 7.50981 3.94962 7.0314 3.85374 6.56964L2.9802 6.88758L2.29616 5.0082L3.16967 4.69027C2.81481 4.03017 2.35712 3.43358 1.81802 2.92192C0.682046 4.30324 0 6.07197 0 8C0 9.92803 0.682047 11.6968 1.81802 13.0781Z" fill="#ff0000"/>
                  </svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'Formula 1' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg fill="#0011ff" height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 487.396 487.396" stroke="#0011ff">
                    <g>
                      <path d="M365.789,250.324c-12.297,0-22.302,10.006-22.302,22.304c0,12.297,10.005,22.301,22.302,22.301 c12.297,0,22.302-10.004,22.302-22.301C388.09,260.33,378.086,250.324,365.789,250.324z M365.789,280.928 c-4.578,0-8.302-3.724-8.302-8.301c0-4.579,3.725-8.304,8.302-8.304c4.578,0,8.302,3.725,8.302,8.304 C374.09,277.205,370.366,280.928,365.789,280.928z"/>
                      <path d="M480.396,262.494h-6.083V250.96c0-3.137-2.086-5.89-5.105-6.738l-71.258-20.035c-0.642-0.181-1.287-0.26-1.922-0.258 c-8.785-5.475-19.146-8.65-30.238-8.65c-7.022,0-13.749,1.274-19.972,3.594c-0.11-0.005-0.219-0.017-0.33-0.017h-75.298v-4.23 c0.666-2.907,3.251-4.986,6.263-4.986h11.708c1.148,0,2.24-0.493,2.999-1.352c0.759-0.86,1.112-2.006,0.97-3.144 c-1.949-15.574-15.264-27.318-30.973-27.318c-4.861,0-9.657,1.145-13.987,3.316v-8.4c0-3.866-3.134-7-7-7 c-57.5,0-112.723,12.182-164.273,36.204v-37.524c0-3.866-3.134-7-7-7H7c-3.866,0-7,3.134-7,7v29.175 c0,2.52,1.354,4.845,3.545,6.088l20.722,11.762v86.951c0,3.866,3.134,7,7,7h64.8c10.375,14.844,27.579,24.579,47.023,24.579 c19.444,0,36.647-9.735,47.022-24.579h128.652c10.375,14.844,27.579,24.579,47.023,24.579c19.444,0,36.647-9.735,47.022-24.579 h67.585c3.866,0,7-3.134,7-7v-28.902C487.396,265.628,484.262,262.494,480.396,262.494z M460.312,262.494h-38.091 c-1.217-6.791-3.627-13.17-7.015-18.912l45.105,12.683V262.494z M200.417,271.812c-0.44-31.247-25.979-56.534-57.327-56.534 c-7.019,0-13.744,1.273-19.965,3.592l-19.708-13.5c12.893-5.055,26.003-9.364,39.308-12.93c0.249,0.23,0.51,0.45,0.798,0.647 l143.521,98.309h-58.037L200.417,271.812z M311.548,291.211l-150.61-103.164c23.551-5.044,47.668-7.796,72.23-8.235v18.627 c0,18.979,15.44,34.418,34.418,34.418h56.944c-9.951,10.319-16.09,24.338-16.09,39.771 C308.44,279.131,309.544,285.379,311.548,291.211z M61.357,291.396h-23.09v-84.027c0-2.52-1.354-4.844-3.545-6.088L14,189.519 v-18.099h47.895v119.975H61.357z M143.09,315.975c-23.902,0-43.348-19.445-43.348-43.348c0-23.903,19.446-43.35,43.348-43.35 c23.902,0,43.347,19.446,43.347,43.35c0,7.558-1.948,14.667-5.363,20.859c-0.764,0.775-1.348,1.727-1.685,2.791 C171.645,308.125,158.271,315.975,143.09,315.975z M365.789,315.975c-23.902,0-43.348-19.445-43.348-43.348 c0-23.902,19.446-43.349,43.348-43.349c23.902,0,43.347,19.446,43.347,43.349C409.136,296.53,389.69,315.975,365.789,315.975z M473.396,291.396h-53.43c1.64-4.721,2.678-9.717,3.025-14.902h50.405V291.396z"/>
                      <path d="M143.09,250.324c-12.297,0-22.302,10.006-22.302,22.304c0,12.297,10.005,22.301,22.302,22.301 c12.297,0,22.302-10.004,22.302-22.301C165.392,260.33,155.388,250.324,143.09,250.324z M143.09,280.928 c-4.578,0-8.302-3.724-8.302-8.301c0-4.579,3.725-8.304,8.302-8.304c4.578,0,8.302,3.725,8.302,8.304 C151.392,277.205,147.668,280.928,143.09,280.928z"/>
                    </g>
                  </svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'NHL' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="-10.88 -10.88 53.76 53.76" fill="#ff0000" stroke="#ff0000">
                    <g>
                      <path d="M5,22c-2.9,0-5-1.3-5-3v-2c0-1.7,2.1-3,5-3s5,1.3,5,3v2C10,20.7,7.9,22,5,22z"/>
                    </g>
                    <g>
                      <path d="M21.8,10l-6.2,13.5c-0.1,0.3-0.5,0.5-0.8,0.5H5.5c-1.9,0-3.6,1.2-4.2,3l-0.4,1.1c-0.3,0.9-0.2,1.9,0.4,2.6 C1.8,31.6,2.7,32,3.7,32H14c2.2,0,4.3-1.3,5.2-3.3L27.9,10H21.8z"/>
                      <path d="M28.8,8l2.3-4.9C31.5,2.4,31.4,1.6,31,1s-1.1-1-1.8-1h-1.4c-0.8,0-1.6,0.5-2,1.3L22.7,8H28.8z"/>
                    </g>
                  </svg>
                </span>
                {/* Badge counts temporarily hidden for mobile - will implement later */}
                {/* {isMobile && (() => {
                  const count = getLeagueCount(league.displayName, sportsEvents, sportsProps);
                  return count > 0 ? (
                    <span className="league-count-badge">
                      {count}
                    </span>
                  ) : null;
                })()} */}
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'Tennis' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.182 13.0781C15.318 11.6968 16 9.92803 16 8C16 6.07197 15.318 4.30324 14.182 2.92192C12.838 4.19745 12 6.00086 12 8C12 9.99914 12.838 11.8025 14.182 13.0781Z" fill="#03c200"/>
                    <path d="M12.7277 14.4543C11.0452 12.819 10 10.5316 10 8C10 5.46844 11.0452 3.18101 12.7277 1.54571C11.4032 0.573931 9.76863 0 8 0C6.23137 0 4.59679 0.573931 3.27234 1.54571C4.95478 3.18101 6 5.46844 6 8C6 10.5316 4.95478 12.819 3.27234 14.4543C4.59679 15.4261 6.23137 16 8 16C9.76863 16 11.4032 15.4261 12.7277 14.4543Z" fill="#03c200"/>
                    <path d="M0 8C0 6.07197 0.682046 4.30324 1.81802 2.92192C3.16196 4.19745 4 6.00086 4 8C4 9.99914 3.16196 11.8025 1.81802 13.0781C0.682047 11.6968 0 9.92803 0 8Z" fill="#03c200"/>
                  </svg>
                </span>
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'Golf' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <ellipse cx="12" cy="18.5" rx="10" ry="3.5" stroke="#003cff" strokeWidth="1.5"></ellipse>
                    <path d="M12 18V2" stroke="#003cff" strokeWidth="1.5" strokeLinecap="round"></path>
                    <path d="M12 3.5L17.4224 6.21114C18.9835 6.99169 19.764 7.38196 19.764 8C19.764 8.61804 18.9835 9.00831 17.4224 9.78886L12 12.5" stroke="#003cff" strokeWidth="1.5" strokeLinecap="round"></path>
                  </svg>
                </span>
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'Champions League' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#002aff" stroke="#002aff">
                    <g>
                      <path d="M464.16,107.059l0.067,0.084l-0.134-0.194c0.016,0.016,0.025,0.042,0.042,0.058 c-31.462-43.992-76.546-77.562-129.185-94.562l0.067,0.016C310.134,4.328,283.538,0,256,0c-27.53,0-54.135,4.328-79.009,12.47 l0.059-0.025c-52.631,17-97.714,50.571-129.177,94.571l0.033-0.042C17.824,148.756,0.092,200.168,0,255.621v0.034V256 c-0.008,57.134,18.748,110.016,50.495,152.537c30.984,41.748,74.387,73.74,124.882,90.446c-0.016,0-0.034,0-0.042-0.009 l0.354,0.118l-0.286-0.101C200.798,507.488,227.932,512,256,512c28.151,0,55.302-4.512,80.748-13.11 c50.403-16.638,93.899-48.63,124.882-90.394l-0.016,0.025C493.269,366,512.008,313.126,512,256v-0.345v-0.025 C511.916,200.235,494.302,148.84,464.16,107.059z M264.622,76.008l47.328-34.336c4.16,1.084,8.311,2.176,12.361,3.504l0.068,0.025 c45.462,14.681,84.529,43.748,111.756,81.832l0.068,0.092c2.504,3.479,4.823,7.101,7.126,10.723l-17.942,54.942l-101.512,33.184 l-59.252-43.21V76.008z M75.84,127.076l0.033-0.042c27.228-38.084,66.294-67.151,111.757-81.832l0.067-0.025 c4.05-1.328,8.202-2.42,12.361-3.504l47.352,34.361v106.731l-59.235,43.21L86.698,192.798l-18-54.95 C71,134.202,73.327,130.571,75.84,127.076z M86.336,398.244c-2.815-3.354-5.614-6.732-8.227-10.252l-0.034-0.042 C50.613,351.126,34.428,305.571,34.42,256v-0.311c0.008-4.344,0.277-8.614,0.529-12.891l46.428-33.639l101.538,33.202 l22.698,69.386l-62.765,86.496H86.336z M338.05,461.689c-4.008,1.588-8.025,3.185-12.142,4.538l-0.127,0.042 c-21.874,7.395-45.286,11.312-69.781,11.312c-24.395,0-47.832-3.916-69.74-11.244l-0.126-0.041 c-4.109-1.361-8.109-2.95-12.109-4.547L156.73,408.42l62.815-86.564h72.815l62.53,87.765L338.05,461.689z M477.58,256 c-0.009,49.58-16.194,95.142-43.58,131.966l-0.016,0.026c-2.614,3.52-5.412,6.89-8.227,10.252h-57.84l-61.521-86.345l22.74-69.547 l101.546-33.202l46.369,33.639c0.252,4.269,0.521,8.546,0.53,12.891V256z"/>
                    </g>
                  </svg>
                </span>
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'FIFA Friendlies' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#002aff" stroke="#002aff">
                    <g>
                      <path d="M464.16,107.059l0.067,0.084l-0.134-0.194c0.016,0.016,0.025,0.042,0.042,0.058 c-31.462-43.992-76.546-77.562-129.185-94.562l0.067,0.016C310.134,4.328,283.538,0,256,0c-27.53,0-54.135,4.328-79.009,12.47 l0.059-0.025c-52.631,17-97.714,50.571-129.177,94.571l0.033-0.042C17.824,148.756,0.092,200.168,0,255.621v0.034V256 c-0.008,57.134,18.748,110.016,50.495,152.537c30.984,41.748,74.387,73.74,124.882,90.446c-0.016,0-0.034,0-0.042-0.009 l0.354,0.118l-0.286-0.101C200.798,507.488,227.932,512,256,512c28.151,0,55.302-4.512,80.748-13.11 c50.403-16.638,93.899-48.63,124.882-90.394l-0.016,0.025C493.269,366,512.008,313.126,512,256v-0.345v-0.025 C511.916,200.235,494.302,148.84,464.16,107.059z M264.622,76.008l47.328-34.336c4.16,1.084,8.311,2.176,12.361,3.504l0.068,0.025 c45.462,14.681,84.529,43.748,111.756,81.832l0.068,0.092c2.504,3.479,4.823,7.101,7.126,10.723l-17.942,54.942l-101.512,33.184 l-59.252-43.21V76.008z M75.84,127.076l0.033-0.042c27.228-38.084,66.294-67.151,111.757-81.832l0.067-0.025 c4.05-1.328,8.202-2.42,12.361-3.504l47.352,34.361v106.731l-59.235,43.21L86.698,192.798l-18-54.95 C71,134.202,73.327,130.571,75.84,127.076z M86.336,398.244c-2.815-3.354-5.614-6.732-8.227-10.252l-0.034-0.042 C50.613,351.126,34.428,305.571,34.42,256v-0.311c0.008-4.344,0.277-8.614,0.529-12.891l46.428-33.639l101.538,33.202 l22.698,69.386l-62.765,86.496H86.336z M338.05,461.689c-4.008,1.588-8.025,3.185-12.142,4.538l-0.127,0.042 c-21.874,7.395-45.286,11.312-69.781,11.312c-24.395,0-47.832-3.916-69.74-11.244l-0.126-0.041 c-4.109-1.361-8.109-2.95-12.109-4.547L156.73,408.42l62.815-86.564h72.815l62.53,87.765L338.05,461.689z M477.58,256 c-0.009,49.58-16.194,95.142-43.58,131.966l-0.016,0.026c-2.614,3.52-5.412,6.89-8.227,10.252h-57.84l-61.521-86.345l22.74-69.547 l101.546-33.202l46.369,33.639c0.252,4.269,0.521,8.546,0.53,12.891V256z"/>
                    </g>
                  </svg>
                </span>
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'Cricket' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg fill="#ff0000" height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 502 502" stroke="#ff0000">
                    <g>
                      <g>
                        <g>
                          <path d="M499.071,428.845l-28.397-28.397c-1.875-1.875-4.419-2.929-7.071-2.929c-2.652,0-5.196,1.054-7.071,2.929l-2.868,2.868 l-97.2-97.199v-51.232c0-2.652-1.054-5.195-2.929-7.071L115.71,9.986C109.27,3.547,100.708,0,91.6,0 c-9.108,0-17.67,3.547-24.11,9.986L9.986,67.49C3.546,73.931,0,82.493,0,91.601c0,9.106,3.546,17.669,9.986,24.109 l237.826,237.826c1.876,1.875,4.419,2.929,7.071,2.929h51.232l97.199,97.199l-2.867,2.867c-3.905,3.905-3.905,10.237,0,14.143 l28.397,28.397c1.875,1.875,4.419,2.929,7.071,2.929c2.652,0,5.196-1.054,7.071-2.929l56.084-56.084 C502.976,439.082,502.976,432.75,499.071,428.845z M306.115,336.465h-47.09L24.129,101.567C21.466,98.905,20,95.365,20,91.601 c0-3.766,1.466-7.306,4.129-9.968l57.504-57.504C84.295,21.466,87.835,20,91.6,20c3.765,0,7.305,1.466,9.967,4.129 l234.897,234.897v47.089L306.115,336.465z M324.401,346.464l22.064-22.064l93.057,93.057l-22.064,22.064L324.401,346.464z M435.916,477.857l-14.255-14.255l2.859-2.858c0.003-0.003,0.007-0.006,0.01-0.009l24.743-24.743l14.331-14.331l14.255,14.255 L435.916,477.857z"/>
                          <path d="M114.29,136.354c-3.905-3.904-10.237-3.904-14.143,0c-3.905,3.905-3.905,10.237,0,14.143L264.141,314.49 c1.953,1.952,4.512,2.929,7.071,2.929s5.119-0.977,7.071-2.929c3.905-3.905,3.905-10.237,0-14.142L114.29,136.354z"/>
                          <path d="M70.274,92.338c-3.905-3.904-10.237-3.904-14.142,0c-3.905,3.905-3.905,10.237,0,14.143l17.749,17.748 c1.953,1.952,4.512,2.929,7.071,2.929c2.559,0,5.119-0.977,7.071-2.929c3.905-3.905,3.905-10.237,0-14.143L70.274,92.338z"/>
                          <path d="M156.5,316c-33.911,0-61.5,27.589-61.5,61.5s27.589,61.5,61.5,61.5s61.5-27.589,61.5-61.5S190.411,316,156.5,316z M156.5,419c-22.883,0-41.5-18.617-41.5-41.5s18.617-41.5,41.5-41.5s41.5,18.617,41.5,41.5S179.383,419,156.5,419z"/>
                        </g>
                      </g>
                    </g>
                  </svg>
                </span>
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'Boxing' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.48901 17.7273H18.3556M8.48901 17.7273V21H18.3556V17.7273M8.48901 17.7273C5.20016 15.5455 3.55573 10.0909 4.10387 8.45455C4.54239 7.14545 6.47916 7.54545 7.39273 7.90909C7.39273 4.09091 9.03715 3 13.4223 3C17.8074 3 20 4.09091 20 9.54545C20 13.9091 18.9037 16.8182 18.3556 17.7273" stroke="#ff0000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                    <path d="M7.39282 7.90909C7.75825 8.27272 8.81799 9 10.1335 9C11.4491 9 13.9705 9 15.0668 9" stroke="#ff0000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                    <path d="M7.39282 7.90909C7.39282 11.7273 9.03725 12.2727 10.1335 12.2727" stroke="#ff0000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                  </svg>
                </span>
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'Chess' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 0C12.5523 0 13 0.447715 13 1V2H14C14.5523 2 15 2.44772 15 3C15 3.55228 14.5523 4 14 4H13L13 6.13426C14.1873 6.46304 15.1164 7.36761 15.6095 8.53557C16.7887 7.70672 18.0146 7.33092 19.1893 7.38765C20.8173 7.46629 22.1801 8.37142 22.9824 9.70379C24.5267 12.2682 23.9536 16.1461 20.2565 19.277L21.1708 21.1056C21.8357 22.4354 20.8688 24 19.382 24H4.61805C3.13129 24 2.1643 22.4354 2.82919 21.1056L3.74347 19.277C0.0464058 16.1461 -0.526611 12.2682 1.01767 9.70378C1.82002 8.37141 3.18278 7.46628 4.8108 7.38766C5.9855 7.33093 7.21132 7.70675 8.3906 8.5356C8.88363 7.36763 9.81271 6.46305 11 6.13427L11 4H10C9.44771 4 9 3.55228 9 3C9 2.44772 9.44771 2 10 2H11V1C11 0.447715 11.4477 0 12 0ZM8.01285 10.8098C6.84088 9.69153 5.74956 9.34466 4.90727 9.38533C4.0034 9.42898 3.2202 9.92317 2.73099 10.7355C1.80208 12.2781 1.87787 15.2648 5.34014 18H10.4437C10.1872 17.422 9.88055 16.7115 9.57338 15.9543C9.19326 15.0173 8.80704 13.9956 8.51427 13.0583C8.27053 12.2779 8.06505 11.476 8.01285 10.8098ZM13.5564 18H18.6599C22.1222 15.2648 22.198 12.2781 21.2691 10.7355C20.7799 9.92318 19.9967 9.42898 19.0928 9.38533C18.2505 9.34464 17.1592 9.6915 15.9872 10.8097C15.935 11.4759 15.7296 12.2779 15.4858 13.0583C15.193 13.9956 14.8068 15.0173 14.4267 15.9543C14.1195 16.7115 13.8129 17.422 13.5564 18ZM18.382 20H12H5.61804L4.61805 22H19.382L18.382 20ZM12 16.5687C12.1822 16.1478 12.3779 15.6844 12.5734 15.2025C12.9433 14.2907 13.307 13.3255 13.5768 12.462C13.8564 11.5668 14 10.8867 14 10.5C14 10.4699 13.9996 10.4401 13.9989 10.4105C13.959 8.87922 12.9603 8 12 8C11.0398 8 10.0411 8.87924 10.0012 10.4105C10.0004 10.4401 10 10.47 10 10.5C10 10.8867 10.1437 11.5668 10.4233 12.462C10.693 13.3255 11.0568 14.2907 11.4267 15.2025C11.6222 15.6844 11.8179 16.1478 12 16.5687Z" fill="#ff0000"></path>
                  </svg>
                </span>
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : league.displayName === 'UFC' ? (
              <div key={`${league.name}-with-icon`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#000000">
                    <style type="text/css">{`.st0{fill:#ff0000;}`}</style>
                    <g>
                      <path className="st0" d="M485.614,188.849c-25.472-11.892-46.128-16.635-52.926-23.064c-6.798-6.437-3.932-23.072-13.859-22.802 c-9.927,0.262-18.51,15.029-17.822,22.646c0.655,7.371,20.771,22.146,20.771,22.146l-31.828-10.91c0,0-3.211-21.107-0.704-26.471 c2.49-5.365,20.918-34.318-10.025-52.034c-30.927-17.707-62.493,14.923-51.223,64.294c0,0-56.677-2.458-82.476-7.552 c-9.73-1.925-71.224-20.1-79.971-25.816c-8.764-5.717-20.394-19.313-33.171-26.914c-12.794-7.601,3.915,23.064,14.923,37.987 c3.489,6.536-2.358,5.046-13.285,4.84c-7.387-0.139-8.174,9.656,9.076,11.18c12.17,1.064,34.514,3.03,34.514,3.03 s36.054,19.23,55.154,24.26c26.553,6.994,54.204,7.813,54.204,7.813c-9.059,14.153-8.895,32.467-14.94,43.851 c-10.745,20.197-37.922,5.979-37.922,5.979s-74.647-36.889-80.626-38.052c-5.995-1.163-94.844-37.356-101.38-43.081 c-2.572-2.244,35.645-28.306,17.822-35.644c-13.76-5.66-37.234,0.844-48.684,18.371C9.802,160.429-5.137,171.42,1.744,181.167 c6.88,9.746,19.493,10.729,24.145,11.27c7.306,0.835,77.939,48.593,105.049,59.118c20.738,8.043,91.094,57.628,91.094,57.628 c10.893,4.636,48.192,43.72,48.192,43.72s-44.67-4.03-51.992-5.816c-7.338-1.793-30.288-30.468-35.743-36.283 c-5.455-5.807-13.875,1.704-11.712,7.592c-2.424,8.674-6.683,28.331,5.472,37.266s36.251,24.678,36.251,24.678 s73.828,31.632,104.165,37.52c31.451,6.102,56.678-31.934,38.331-64.597c-11.827-21.034-14.84-40.821-14.84-40.821 s30.844-16.217,66.326-63.361c26.995-5.357,88.62-3.538,100.152-17.757C518.162,217.106,511.102,200.75,485.614,188.849z"/>
                    </g>
                  </svg>
                </span>
                <button
                  className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
                >
                  {league.displayName}
                </button>
              </div>
            ) : (
              <button
                key={league.name}
                className={`sports-league-button ${selectedSubCategory === (league.apiKey || league.displayName) ? 'active' : ''}`}
                onClick={() => onSubCategoryChange(league.apiKey || league.displayName)}
              >
                {league.displayName}
              </button>
            )
          ))}
          {/* Soccer sub-leagues */}
          {SPORTS_CATEGORIES.find(c => c.name === 'Soccer')?.subcategories?.filter(sub =>
            // Skip Premier League, La Liga, Ligue 1, MLS, Champions League, and FIFA Friendlies here as they're already shown as standalone
            sub !== 'Premier League' && sub !== 'La Liga' && sub !== 'Ligue 1' && sub !== 'MLS' && sub !== 'Champions League' && sub !== 'FIFA Friendlies'
          ).map(sub => {
            const soccerIconLeagues = new Set([
              'La Liga',
              'Bundesliga',
              'Serie A',
              'Ligue 1',
              'MLS',
              'FIFA Friendlies',
              'Champions League'
            ]);
            return soccerIconLeagues.has(sub) ? (
              <div key={`soccer-${sub}`} className="league-with-icon">
                <span className="league-icon" aria-hidden="true">
                  <svg height="200px" width="200px" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="#002aff" stroke="#002aff">
                    <g>
                      <path d="M464.16,107.059l0.067,0.084l-0.134-0.194c0.016,0.016,0.025,0.042,0.042,0.058 c-31.462-43.992-76.546-77.562-129.185-94.562l0.067,0.016C310.134,4.328,283.538,0,256,0c-27.53,0-54.135,4.328-79.009,12.47 l0.059-0.025c-52.631,17-97.714,50.571-129.177,94.571l0.033-0.042C17.824,148.756,0.092,200.168,0,255.621v0.034V256 c-0.008,57.134,18.748,110.016,50.495,152.537c30.984,41.748,74.387,73.74,124.882,90.446c-0.016,0-0.034,0-0.042-0.009 l0.354,0.118l-0.286-0.101C200.798,507.488,227.932,512,256,512c28.151,0,55.302-4.512,80.748-13.11 c50.403-16.638,93.899-48.63,124.882-90.394l-0.016,0.025C493.269,366,512.008,313.126,512,256v-0.345v-0.025 C511.916,200.235,494.302,148.84,464.16,107.059z M264.622,76.008l47.328-34.336c4.16,1.084,8.311,2.176,12.361,3.504l0.068,0.025 c45.462,14.681,84.529,43.748,111.756,81.832l0.068,0.092c2.504,3.479,4.823,7.101,7.126,10.723l-17.942,54.942l-101.512,33.184 l-59.252-43.21V76.008z M75.84,127.076l0.033-0.042c27.228-38.084,66.294-67.151,111.757-81.832l0.067-0.025 c4.05-1.328,8.202-2.42,12.361-3.504l47.352,34.361v106.731l-59.235,43.21L86.698,192.798l-18-54.95 C71,134.202,73.327,130.571,75.84,127.076z M86.336,398.244c-2.815-3.354-5.614-6.732-8.227-10.252l-0.034-0.042 C50.613,351.126,34.428,305.571,34.42,256v-0.311c0.008-4.344,0.277-8.614,0.529-12.891l46.428-33.639l101.538,33.202 l22.698,69.386l-62.765,86.496H86.336z M338.05,461.689c-4.008,1.588-8.025,3.185-12.142,4.538l-0.127,0.042 c-21.874,7.395-45.286,11.312-69.781,11.312c-24.395,0-47.832-3.916-69.74-11.244l-0.126-0.041 c-4.109-1.361-8.109-2.95-12.109-4.547L156.73,408.42l62.815-86.564h72.815l62.53,87.765L338.05,461.689z M477.58,256 c-0.009,49.58-16.194,95.142-43.58,131.966l-0.016,0.026c-2.614,3.52-5.412,6.89-8.227,10.252h-57.84l-61.521-86.345l22.74-69.547 l101.546-33.202l46.369,33.639c0.252,4.269,0.521,8.546,0.53,12.891V256z"/>
                      <path className="st0" d="M336.806,498.874c-0.025,0.008-0.042,0.008-0.058,0.016c-0.026,0.008-0.051,0.017-0.068,0.026L336.806,498.874 z"/>
                    </g>
                  </svg>
                </span>
                <button
                  className={`sports-league-button ${selectedSubCategory === sub ? 'active' : ''}`}
                  onClick={() => onSubCategoryChange(sub)}
                >
                  {sub}
                </button>
              </div>
            ) : (
              <button
                key={`soccer-${sub}`}
                className={`sports-league-button ${selectedSubCategory === sub ? 'active' : ''}`}
                onClick={() => onSubCategoryChange(sub)}
              >
                {sub}
              </button>
            );
          })}
        </div>
      )}
      {/* Combined Sub-navigation Row: left filter, centered subcategories */}
      {(selectedCategory === 'Politics' || selectedCategory === 'Culture' || selectedCategory === 'Geopolitics' || 
        selectedCategory === 'Tech' || selectedCategory === 'Economics' || 
        selectedCategory === 'World') && (
        <div className={`subnav-row ${isTrendingDropdownOpen ? 'filters-open' : 'filters-closed'}`}>
          {/* Left: Filter (always show on subcategory pages) */}
          {true ? (
            <div className="filter-section-inline">
              <div className="filter-buttons">
                <div className="filter-dropdown" ref={trendingDropdownRef}>
                  <button 
                    className="filter-button"
                    onClick={() => setIsTrendingDropdownOpen(!isTrendingDropdownOpen)}
                  >
                    {isMobile ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10.9769 7.5V5.83333H13.4769V2.5H15.1436V5.83333H17.6436V7.5H10.9769ZM13.4769 17.5V9.16667H15.1436V17.5H13.4769ZM5.14355 17.5V14.1667H2.64355V12.5H9.31022V14.1667H6.81022V17.5H5.14355ZM5.14355 10.8333V2.5H6.81022V10.8333H5.14355Z"/>
                      </svg>
                    ) : selectedFilter ? (
                      <>
                        <span>{selectedFilter}</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6,9 12,15 18,9"/>
                        </svg>
                      </>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10.9769 7.5V5.83333H13.4769V2.5H15.1436V5.83333H17.6436V7.5H10.9769ZM13.4769 17.5V9.16667H15.1436V17.5H13.4769ZM5.14355 17.5V14.1667H2.64355V12.5H9.31022V14.1667H6.81022V17.5H5.14355ZM5.14355 10.8333V2.5H6.81022V10.8333H5.14355Z"/>
                      </svg>
                    )}
                  </button>
                  {isTrendingDropdownOpen && (
                    <div className="filter-dropdown-menu">
                      {!isMobile && (
                        <label className="filter-option">
                          <input
                            type="radio"
                            name="trending-filter"
                            value=""
                            checked={selectedFilter === ''}
                            onChange={(e) => {
                              onFilterChange?.(e.target.value);
                              if (isMobile) {
                                setIsTrendingDropdownOpen(true);
                              } else {
                                setIsTrendingDropdownOpen(false);
                              }
                            }}
                          />
                          <span className="radio-custom"></span>
                          <span className="option-text">None</span>
                        </label>
                      )}
                      {false && selectedCategory === 'Sports' && (
                        <label className="filter-option">
                          <input
                            type="radio"
                            name="trending-filter"
                            value="Live"
                            checked={selectedFilter === 'Live'}
                            onChange={(e) => {
                              onFilterChange?.(e.target.value);
                              if (isMobile) {
                                setIsTrendingDropdownOpen(true);
                              } else {
                                setIsTrendingDropdownOpen(false);
                              }
                            }}
                          />
                          <span className="radio-custom"></span>
                          <span className="option-text">Live</span>
                        </label>
                      )}
                      <label className="filter-option">
                        <input
                          type="radio"
                          name="trending-filter"
                          value="Trending"
                          checked={selectedFilter === 'Trending'}
                          onClick={(e) => {
                            console.log('Trending onClick fired, isMobile:', isMobile, 'selectedFilter:', selectedFilter);
                            if (selectedFilter === 'Trending') {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('Deselecting Trending filter');
                              onFilterChange?.('');
                              if (isMobile) {
                                console.log('Keeping dropdown open on mobile after deselect');
                                // Use setTimeout to ensure this runs after the parent re-renders
                                setTimeout(() => setIsTrendingDropdownOpen(true), 0);
                              }
                            }
                          }}
                          onChange={(e) => {
                            console.log('🎯 Trending onChange fired:', { 
                              value: e.target.value, 
                              isMobile, 
                              selectedFilter 
                            });
                            onFilterChange?.(e.target.value);
                            if (isMobile) {
                              console.log('📱 Mobile: Setting dropdown to open after Trending selection');
                              setIsTrendingDropdownOpen(true);
                            } else {
                              console.log('🖥️ Desktop: Setting dropdown to closed after Trending selection');
                              setIsTrendingDropdownOpen(false);
                            }
                          }}
                        />
                        <span className="radio-custom"></span>
                        <span className="option-text">Trending</span>
                      </label>
                      <label className="filter-option">
                        <input
                          type="radio"
                          name="trending-filter"
                          value="Competitive"
                          checked={selectedFilter === 'Competitive'}
                          onClick={(e) => {
                            if (selectedFilter === 'Competitive') {
                              e.preventDefault();
                              e.stopPropagation();
                              onFilterChange?.('');
                              if (isMobile) {
                                setTimeout(() => setIsTrendingDropdownOpen(true), 0);
                              }
                            }
                          }}
                          onChange={(e) => {
                            onFilterChange?.(e.target.value);
                            if (isMobile) {
                              setIsTrendingDropdownOpen(true);
                            } else {
                              setIsTrendingDropdownOpen(false);
                            }
                          }}
                        />
                        <span className="radio-custom"></span>
                        <span className="option-text">Competitive</span>
                      </label>
                      <label className="filter-option">
                        <input
                          type="radio"
                          name="trending-filter"
                          value="Ending soon"
                          checked={selectedFilter === 'Ending soon'}
                          onClick={(e) => {
                            if (selectedFilter === 'Ending soon') {
                              e.preventDefault();
                              e.stopPropagation();
                              onFilterChange?.('');
                              if (isMobile) {
                                setTimeout(() => setIsTrendingDropdownOpen(true), 0);
                              }
                            }
                          }}
                          onChange={(e) => {
                            onFilterChange?.(e.target.value);
                            if (isMobile) {
                              setIsTrendingDropdownOpen(true);
                            } else {
                              setIsTrendingDropdownOpen(false);
                            }
                          }}
                        />
                        <span className="radio-custom"></span>
                        <span className="option-text">Ending soon</span>
                      </label>
                      <label className="filter-option">
                        <input
                          type="radio"
                          name="trending-filter"
                          value="Newest"
                          checked={selectedFilter === 'Newest'}
                          onClick={(e) => {
                            if (selectedFilter === 'Newest') {
                              e.preventDefault();
                              e.stopPropagation();
                              onFilterChange?.('');
                              if (isMobile) {
                                setTimeout(() => setIsTrendingDropdownOpen(true), 0);
                              }
                            }
                          }}
                          onChange={(e) => {
                            onFilterChange?.(e.target.value);
                            if (isMobile) {
                              setIsTrendingDropdownOpen(true);
                            } else {
                              setIsTrendingDropdownOpen(false);
                            }
                          }}
                        />
                        <span className="radio-custom"></span>
                        <span className="option-text">Newest</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div></div>
          )}

          {/* Center: Secondary Categories Row */}
          {selectedCategory === 'Politics' && (
            <div className="secondary-categories-nav">
          <button 
            className={`secondary-category-button ${selectedSubCategory === '' || !selectedSubCategory ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange(''); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Unknown', sub_category: 'All' }); } catch {} }}
          >
            All
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Trump Presidency' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Trump Presidency'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Politics', sub_category: 'Trump Presidency' }); } catch {} }}
          >
            Trump Presidency
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Ukraine' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Ukraine'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Politics', sub_category: 'Ukraine' }); } catch {} }}
          >
            Ukraine
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'NYC Mayor' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('NYC Mayor'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Politics', sub_category: 'NYC Mayor' }); } catch {} }}
          >
            NYC Mayor
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Gov Shutdown' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Gov Shutdown'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Politics', sub_category: 'Gov Shutdown' }); } catch {} }}
          >
            Gov Shutdown
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Global Elections' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Global Elections'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Politics', sub_category: 'Global Elections' }); } catch {} }}
          >
            Global Elections
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'World' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('World'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Politics', sub_category: 'World' }); } catch {} }}
          >
            World
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'France' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('France'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Politics', sub_category: 'France' }); } catch {} }}
          >
            France
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'EU' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('EU'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Politics', sub_category: 'EU' }); } catch {} }}
          >
            EU
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'China' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('China'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Politics', sub_category: 'China' }); } catch {} }}
          >
            China
          </button>
            </div>
          )}
          {selectedCategory === 'Culture' && (
            <div className="secondary-categories-nav">
          <button 
            className={`secondary-category-button ${selectedSubCategory === '' || !selectedSubCategory ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange(''); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Culture', sub_category: 'All' }); } catch {} }}
          >
            All
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Youtube' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Youtube'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Culture', sub_category: 'Youtube' }); } catch {} }}
          >
            Youtube
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'MrBeast' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('MrBeast'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Culture', sub_category: 'MrBeast' }); } catch {} }}
          >
            MrBeast
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Box Office' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Box Office'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Culture', sub_category: 'Box Office' }); } catch {} }}
          >
            Box Office
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Movies' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Movies'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Culture', sub_category: 'Movies' }); } catch {} }}
          >
            Movies
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Music' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Music'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Culture', sub_category: 'Music' }); } catch {} }}
          >
            Music
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Courts' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Courts'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Culture', sub_category: 'Courts' }); } catch {} }}
          >
            Courts
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Hollywood' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Hollywood'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Culture', sub_category: 'Hollywood' }); } catch {} }}
          >
            Hollywood
          </button>
            </div>
          )}
          {selectedCategory === 'Geopolitics' && (
            <div className="secondary-categories-nav">
          <button 
            className={`secondary-category-button ${selectedSubCategory === '' || !selectedSubCategory ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange(''); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'All' }); } catch {} }}
          >
            All
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Ukraine' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Ukraine'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'Ukraine' }); } catch {} }}
          >
            Ukraine
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Israel' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Israel'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'Israel' }); } catch {} }}
          >
            Israel
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'China' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('China'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'China' }); } catch {} }}
          >
            China
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Middle East' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Middle East'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'Middle East' }); } catch {} }}
          >
            Middle East
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'World' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('World'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'World' }); } catch {} }}
          >
            World
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Iran' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Iran'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'Iran' }); } catch {} }}
          >
            Iran
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Gaza' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Gaza'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'Gaza' }); } catch {} }}
          >
            Gaza
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Foreign Policy' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Foreign Policy'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'Foreign Policy' }); } catch {} }}
          >
            Foreign Policy
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'India-Pakistan' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('India-Pakistan'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'India-Pakistan' }); } catch {} }}
          >
            India-Pakistan
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Russia' ? 'active' : ''}`}
            onClick={() => { onSubCategoryChange('Russia'); try { posthog.capture('engagement:secondary_category_selected', { category: selectedCategory || 'Geopolitics', sub_category: 'Russia' }); } catch {} }}
          >
            Russia
          </button>
            </div>
          )}
          {selectedCategory === 'Tech' && (
            <div className="secondary-categories-nav" style={{ marginLeft: isMobile ? '0px' : '40px' }}>
          <button 
            className={`secondary-category-button ${selectedSubCategory === '' || !selectedSubCategory ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('')}
          >
            All
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'AI' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('AI')}
          >
            AI
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Elon Musk' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Elon Musk')}
          >
            Elon Musk
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'IPOs' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('IPOs')}
          >
            IPOs
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Google' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Google')}
          >
            Google
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Larry Ellison' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Larry Ellison')}
          >
            Larry Ellison
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'OpenAI' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('OpenAI')}
          >
            OpenAI
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Big Tech' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Big Tech')}
          >
            Big Tech
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Meta vs FTC' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Meta vs FTC')}
          >
            Meta vs FTC
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Tesla' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Tesla')}
          >
            Tesla
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'TikTok' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('TikTok')}
          >
            TikTok
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'SpaceX' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('SpaceX')}
          >
            SpaceX
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Apple' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Apple')}
          >
            Apple
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Science' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Science')}
          >
            Science
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Grok' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Grok')}
          >
            Grok
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Gemini' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Gemini')}
          >
            Gemini
          </button>
            </div>
          )}
          {selectedCategory === 'Economics' && (
            <div className="secondary-categories-nav">
          <button
            className={`secondary-category-button ${selectedSubCategory === '' || !selectedSubCategory ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('')}
          >
            All
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Economic Policy' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Economic Policy')}
          >
            Economic Policy
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Fed' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Fed')}
          >
            Fed
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Powell' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Powell')}
          >
            Powell
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === '2025 Predictions' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('2025 Predictions')}
          >
            2025 Predictions
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Macro Indicators' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Macro Indicators')}
          >
            Macro Indicators
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Treasuries' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Treasuries')}
          >
            Treasuries
          </button>
            </div>
          )}
          {/* Sports navigation is now in the left sidebar - Header nav hidden */}
          {/* Sports subcategory navigation moved to sidebar - code kept for reference
          {selectedCategory === 'Sports' && (
            <div className="secondary-categories-nav" style={{ marginLeft: isMobile ? '0px' : '40px' }}>
          <button 
            className={`secondary-category-button ${selectedSubCategory === '' || !selectedSubCategory ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('')}
          >
            All
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'NFL' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('NFL')}
          >
            NFL
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'MLB' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('MLB')}
          >
            MLB
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'NBA' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('NBA')}
          >
            NBA
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'La Liga' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('La Liga')}
          >
            La Liga
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Cricket' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Cricket')}
          >
            Cricket
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Boxing' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Boxing')}
          >
            Boxing
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'CFB' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('CFB')}
          >
            College Football
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Champions League' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Champions League')}
          >
            Champions League
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Premier League' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Premier League')}
          >
            Premier League
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Bundesliga' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Bundesliga')}
          >
            Bundesliga
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'NHL' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('NHL')}
          >
            NHL
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Tennis' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Tennis')}
          >
            Tennis
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Rugby' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Rugby')}
          >
            Rugby
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'UFC' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('UFC')}
          >
            UFC
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Golf' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Golf')}
          >
            Golf
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'Chess' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Chess')}
          >
            Chess
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'World Cup' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('World Cup')}
          >
            World Cup
          </button>
            </div>
          )}
          */}
          {selectedCategory === 'World' && (
            <div className="secondary-categories-nav">
          <button 
            className={`secondary-category-button ${selectedSubCategory === '' || !selectedSubCategory ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('')}
          >
            All
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Global Elections' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Global Elections')}
          >
            Global Elections
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Israel' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Israel')}
          >
            Israel
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Ukraine' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Ukraine')}
          >
            Ukraine
          </button>
          <button
            className={`secondary-category-button ${selectedSubCategory === 'FIFA World Cup' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('FIFA World Cup')}
          >
            FIFA World Cup
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'South Korea' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('South Korea')}
          >
            South Korea
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Canada' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Canada')}
          >
            Canada
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'China' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('China')}
          >
            China
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Middle East' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Middle East')}
          >
            Middle East
          </button>
          <button 
            className={`secondary-category-button ${selectedSubCategory === 'Gaza' ? 'active' : ''}`}
            onClick={() => onSubCategoryChange('Gaza')}
          >
            Gaza
          </button>
            </div>
          )}

          {/* Right: spacer to balance centering */}
          <div className="subnav-spacer"></div>
        </div>
      )}
    </div>
  );
};

export default Header;